import { ArcType, BoundingSphere, BoxGeometry, Cartesian2, Cartesian3, Cartographic, Math as CesiumMath, Color, ColorGeometryInstanceAttribute, ComponentDatatype, ConstantPositionProperty, CylinderGeometry, Ellipsoid, EllipsoidGeometry, Entity, Geometry, GeometryAttribute, GeometryInstance, GeometryPipeline, HeightReference, HorizontalOrigin, IntersectionTests, LabelStyle, Matrix4, NearFarScalar, PerInstanceColorAppearance, Plane, PolylineArrowMaterialProperty, PolylineOutlineMaterialProperty, Primitive, PrimitiveType, Quaternion, Ray, VertexFormat, VerticalOrigin, Matrix3, sampleTerrainMostDetailed, Transforms, Material, MaterialAppearance, } from "cesium";
import { HeightMapUtils } from "../core/utils";
import { ZBoundedPlane } from "../core/z-bounded-plane";
import { AssetType, } from "../types";
import { CesiumUtils } from "./cesium-utils";
import { intersectGeometryByPlane } from "./geometry-utility";
/* Small offset (in meters) used for existence checks near boundaries */
const BOUNDARY_TOLERANCE = 0.001;
export var EarthBrainUtils;
(function (EarthBrainUtils) {
    /**
     * Function return a point on quadratic bezier curve
     * Refer to this link for more information on quadratic bezier curve: https://en.wikipedia.org/wiki/B%C3%A9zier_curve
     * @param A First point of curve
     * @param B Mid/ControlPoint of curve
     * @param C Last point of curver
     * @param t 0 <= t <= 1 (At what division you need point on bezier curve ex. t = 0.5 will give point on at mid on terrain.)
     * @returns return point on bezier curve
     */
    function drawThreePointBezier(A, B, C, t) {
        // const P = new Cartesian3(0, 0, 0);
        const a1 = Math.pow(1 - t, 2);
        const a2 = (1 - t) * 2 * t;
        const a3 = t * t;
        // P.x = a1 * A.x + a2 * B.x + a3 * C.x;
        // P.y = a1 * A.y + a2 * B.y + a3 * C.y;
        // P.z = a1 * A.z + a2 * B.z + a3 * C.z;
        // return P;
        // P = (Math.pow(1 - t, 2))A + ((1 - t) * 2 * t)B + (t * t)C
        const point = Cartesian3.add(Cartesian3.multiplyByScalar(A, a1, new Cartesian3()), Cartesian3.multiplyByScalar(B, a2, new Cartesian3()), new Cartesian3());
        Cartesian3.add(point, Cartesian3.multiplyByScalar(C, a3, new Cartesian3()), point);
        return point;
    }
    EarthBrainUtils.drawThreePointBezier = drawThreePointBezier;
    /**
     * Function to get bezier points
     * @param points Array of control points (3 points only as we are drawing qudratic curve)
     * @param divisions Division required for bezier curve
     * @param start: Start of the bezier curve (range 0.0 to 1.0)
     * @param end: End of the bezier curve (range 0.0 to 1.0)
     * @returns Array of bezier curve points
     */
    function getBezierPoints(points, divisions, start, end) {
        const bezierPointsArry = [];
        const midCartesianPointsArry = [];
        for (let iter = 0; iter < points.length - 1; iter++) {
            midCartesianPointsArry.push(new Cartesian3((points[iter].x + points[iter + 1].x) / 2, (points[iter].y + points[iter + 1].y) / 2, (points[iter].z + points[iter + 1].z) / 2));
            if (iter + 1 !== points.length - 1) {
                midCartesianPointsArry.push(new Cartesian3(points[iter + 1].x, points[iter + 1].y, points[iter + 1].z));
            }
        }
        //given 3 points in 3D write an function to find center and radius of circle
        // It is possible that Interpolation pitch is greater than that of the arcLength in this case division = arclen/interpolationpitch = 0
        // In this case path array for the segment is midpointCartesian array and projected point index should be 1 becuase it is ref point of current segment
        if (divisions === 0) {
            bezierPointsArry.push(midCartesianPointsArry[0], midCartesianPointsArry[1], midCartesianPointsArry[2]);
        }
        else if (divisions === 1) {
            const P = drawThreePointBezier(midCartesianPointsArry[0], midCartesianPointsArry[1], midCartesianPointsArry[2], 0.5);
            bezierPointsArry.push(midCartesianPointsArry[0], P, midCartesianPointsArry[2]);
        }
        else {
            for (let iter = 0; iter + 2 < midCartesianPointsArry.length; iter += 2) {
                for (let t = start; t <= end; t += 1 / divisions) {
                    const P = drawThreePointBezier(midCartesianPointsArry[iter], midCartesianPointsArry[iter + 1], midCartesianPointsArry[iter + 2], t);
                    bezierPointsArry.push(P);
                }
            }
        }
        return bezierPointsArry;
    }
    EarthBrainUtils.getBezierPoints = getBezierPoints;
    function createLineEntity(viewer, name, points, lineSettings) {
        const lineEntity = CesiumUtils.createPolyLine(points, name, lineSettings.width, new PolylineOutlineMaterialProperty({
            color: lineSettings.color,
            outlineColor: lineSettings.outlineColor,
            outlineWidth: lineSettings.outlineWidth,
        }), lineSettings.clampToGround, lineSettings.show ? lineSettings.show : true);
        return viewer.entities.add(lineEntity);
    }
    EarthBrainUtils.createLineEntity = createLineEntity;
    function addSphere(viewer, position, color, depthTestDistance = 0, pxSize = 3.5, id) {
        if (!position) {
            return;
        }
        const sphereEntity = viewer.entities.add({
            id,
            description: "Preview Point For Path Creation",
            position: position,
            point: {
                pixelSize: pxSize,
                color: color,
                heightReference: HeightReference.NONE,
                scaleByDistance: new NearFarScalar(0, pxSize, 2000, 0),
                disableDepthTestDistance: depthTestDistance,
            },
        });
        sphereEntity.name = "sphereEntity";
        return sphereEntity;
    }
    EarthBrainUtils.addSphere = addSphere;
    function createSphere(id, name, description, position, color, depthTestDistance = 0, pxSize = 3.5) {
        if (!position) {
            return;
        }
        const sphereEntity = new Entity({
            id,
            description,
            position: position,
            name,
            point: {
                pixelSize: pxSize,
                color: color,
                heightReference: HeightReference.NONE,
                scaleByDistance: new NearFarScalar(0, pxSize, 2000, 0),
                disableDepthTestDistance: depthTestDistance,
            },
        });
        return sphereEntity;
    }
    EarthBrainUtils.createSphere = createSphere;
    function createBoxPrimitive(position, dimensions, color, id) {
        const boxGeom = BoxGeometry.fromDimensions({
            dimensions,
            vertexFormat: MaterialAppearance.MaterialSupport.BASIC.vertexFormat,
        });
        const instance = new GeometryInstance({
            geometry: boxGeom,
            modelMatrix: Matrix4.IDENTITY,
            id: {
                id,
                type: "solid-mesh",
                allowpicking: true,
                pickInfo: { assetType: AssetType.SOLID_MESH },
            },
        });
        const primitive = new Primitive({
            geometryInstances: instance,
            modelMatrix: Transforms.eastNorthUpToFixedFrame(position),
            asynchronous: false,
            appearance: new MaterialAppearance({
                material: Material.fromType("Color", {
                    color,
                }),
                translucent: color.alpha < 1.0,
                flat: false,
                faceForward: true,
                closed: true,
                materialSupport: MaterialAppearance.MaterialSupport.BASIC,
            }),
        });
        return primitive;
    }
    EarthBrainUtils.createBoxPrimitive = createBoxPrimitive;
    /**
     * Creates an ellipsoid primitive (supports spheres and ellipsoids)
     * @param position - Center position in local coordinates
     * @param dimensions - Diameter for X, Y, Z axes (use equal values for sphere)
     * @param color - Color of the ellipsoid
     * @param id - Optional ID for picking
     */
    function createEllipsoidPrimitive(position, dimensions, color, id) {
        // Convert dimensions (diameter) to radii by multiplying by 0.5
        const radii = Cartesian3.multiplyByScalar(dimensions, 0.5, new Cartesian3());
        const ellipsoidGeom = new EllipsoidGeometry({
            radii,
            vertexFormat: MaterialAppearance.MaterialSupport.BASIC.vertexFormat,
        });
        const instance = new GeometryInstance({
            geometry: ellipsoidGeom,
            modelMatrix: Matrix4.IDENTITY,
            id: {
                id,
                type: "solid-mesh",
                allowpicking: true,
                pickInfo: { assetType: AssetType.SOLID_MESH },
            },
        });
        const primitive = new Primitive({
            geometryInstances: instance,
            modelMatrix: Transforms.eastNorthUpToFixedFrame(position),
            asynchronous: false,
            appearance: new MaterialAppearance({
                material: Material.fromType("Color", {
                    color,
                }),
                translucent: color.alpha < 1.0,
                flat: false,
                faceForward: true,
                closed: true,
                materialSupport: MaterialAppearance.MaterialSupport.BASIC,
            }),
        });
        return primitive;
    }
    EarthBrainUtils.createEllipsoidPrimitive = createEllipsoidPrimitive;
    /**
     * Creates a cylinder primitive
     * @param position - Center position in local coordinates
     * @param dimensions - Diameter for X/Y and height for Z
     * @param color - Color of the cylinder
     * @param id - Optional ID for picking
     */
    function createCylinderPrimitive(position, dimensions, color, id) {
        const radiusX = Math.abs(dimensions.x) * 0.5;
        const radiusY = Math.abs(dimensions.y) * 0.5;
        // Cesium cylinders are circular; average X/Y diameters to keep it stable.
        const radius = (radiusX + radiusY) * 0.5;
        const length = Math.abs(dimensions.z);
        const cylinderGeom = new CylinderGeometry({
            length,
            topRadius: radius,
            bottomRadius: radius,
            vertexFormat: MaterialAppearance.MaterialSupport.BASIC.vertexFormat,
        });
        const instance = new GeometryInstance({
            geometry: cylinderGeom,
            modelMatrix: Matrix4.IDENTITY,
            id: {
                id,
                type: "solid-mesh",
                allowpicking: true,
                pickInfo: { assetType: AssetType.SOLID_MESH },
            },
        });
        const primitive = new Primitive({
            geometryInstances: instance,
            modelMatrix: Transforms.eastNorthUpToFixedFrame(position),
            asynchronous: false,
            appearance: new MaterialAppearance({
                material: Material.fromType("Color", {
                    color,
                }),
                translucent: color.alpha < 1.0,
                flat: false,
                faceForward: true,
                closed: true,
                materialSupport: MaterialAppearance.MaterialSupport.BASIC,
            }),
        });
        return primitive;
    }
    EarthBrainUtils.createCylinderPrimitive = createCylinderPrimitive;
    function addLabel(viewer, position, text, params) {
        const currCartoGraphicPos = Cartographic.fromCartesian(position.clone());
        //text has same position as control point and that why sometimes it is getting behind flat ground area. So adding height to position. which is 5
        currCartoGraphicPos.height = currCartoGraphicPos.height + 0.5;
        const newCartesianPos = Cartesian3.fromRadians(currCartoGraphicPos.longitude, currCartoGraphicPos.latitude, currCartoGraphicPos.height);
        const label = viewer.entities.add({
            label: {
                show: true,
                text: text,
                font: params && params.fontSize
                    ? `${params.fontSize}px san-serif`
                    : "30px sans-serif",
                fillColor: params && params.fillColor ? params.fillColor : Color.WHITE,
                outlineColor: params && params.outlineColor ? params.outlineColor : Color.BLACK,
                outlineWidth: params && params.outlineWidth ? params.outlineWidth : 1.0,
                showBackground: params && params.showBackground !== undefined
                    ? params.showBackground
                    : false,
                backgroundColor: params && params.backgroundColor
                    ? params.backgroundColor
                    : new Color(0.165, 0.165, 0.165, 0.8),
                backgroundPadding: params && params.backgroundPadding
                    ? params.backgroundPadding
                    : new Cartesian2(0, 0),
                style: LabelStyle.FILL,
                pixelOffset: Cartesian2.ZERO,
                eyeOffset: Cartesian3.ZERO,
                horizontalOrigin: HorizontalOrigin.LEFT,
                verticalOrigin: VerticalOrigin.BASELINE,
                scale: 1.0,
                translucencyByDistance: undefined,
                pixelOffsetScaleByDistance: undefined,
                heightReference: HeightReference.NONE,
                distanceDisplayCondition: undefined,
                disableDepthTestDistance: params && params.disableDepthDistance
                    ? params.disableDepthDistance
                    : 0,
            },
        });
        label.position = new ConstantPositionProperty(newCartesianPos.clone());
        return label;
    }
    EarthBrainUtils.addLabel = addLabel;
    function createLabel(position, text, params) {
        const currCartoGraphicPos = Cartographic.fromCartesian(position.clone());
        //text has same position as control point and that why sometimes it is getting behind flat ground area. So adding height to position. which is 5
        currCartoGraphicPos.height = currCartoGraphicPos.height + 0.5;
        const newCartesianPos = Cartesian3.fromRadians(currCartoGraphicPos.longitude, currCartoGraphicPos.latitude, currCartoGraphicPos.height);
        const label = new Entity({
            label: {
                show: true,
                text: text,
                font: params && params.fontSize
                    ? `${params.fontSize}px san-serif`
                    : "30px sans-serif",
                fillColor: params && params.fillColor ? params.fillColor : Color.WHITE,
                outlineColor: params && params.outlineColor ? params.outlineColor : Color.BLACK,
                outlineWidth: params && params.outlineWidth ? params.outlineWidth : 1.0,
                showBackground: params && params.showBackground !== undefined
                    ? params.showBackground
                    : false,
                backgroundColor: params && params.backgroundColor
                    ? params.backgroundColor
                    : new Color(0.165, 0.165, 0.165, 0.8),
                backgroundPadding: params && params.backgroundPadding
                    ? params.backgroundPadding
                    : new Cartesian2(0, 0),
                style: LabelStyle.FILL,
                pixelOffset: Cartesian2.ZERO,
                eyeOffset: Cartesian3.ZERO,
                horizontalOrigin: HorizontalOrigin.LEFT,
                verticalOrigin: VerticalOrigin.BASELINE,
                scale: 1.0,
                translucencyByDistance: undefined,
                pixelOffsetScaleByDistance: undefined,
                heightReference: HeightReference.NONE,
                distanceDisplayCondition: undefined,
                disableDepthTestDistance: params && params.disableDepthDistance
                    ? params.disableDepthDistance
                    : 0,
            },
        });
        label.position = new ConstantPositionProperty(newCartesianPos.clone());
        return label;
    }
    EarthBrainUtils.createLabel = createLabel;
    function createPlane(viewer, position, planeNormal, dimensions, material) {
        const plane = viewer.entities.add({
            position: position,
            plane: {
                plane: new Plane(planeNormal, 0),
                dimensions: dimensions,
                material: material,
            },
        });
        return plane;
    }
    EarthBrainUtils.createPlane = createPlane;
    function copyCartesian(source, target) {
        target.x = source.x;
        target.y = source.y;
        target.z = source.z;
    }
    EarthBrainUtils.copyCartesian = copyCartesian;
    function surfaceDistanceBetweenTwoPoints(pointA, pointB) {
        const surfaceRadius = 6371000;
        let result = 0;
        //arc cos (sinLambdaA sinLambdaB + cos LambdaA cosLambdaB cosDeltaLongitude)*EarthRadius
        result = Math.cos(pointA.longitude - pointB.longitude);
        result = result * Math.cos(pointA.latitude) * Math.cos(pointB.latitude);
        result = result + Math.sin(pointA.latitude) * Math.sin(pointB.latitude);
        result = Math.acos(result);
        result = result * surfaceRadius;
        return result;
    }
    EarthBrainUtils.surfaceDistanceBetweenTwoPoints = surfaceDistanceBetweenTwoPoints;
    /**
     *
     * @param startPoint - from point
     * @param endPoint - to point
     * @returns
     */
    function getDirectionOfPointOnSitePath(startPoint, endPoint, upDir) {
        //ToDo-2965: Check where to get the up directions. Harcoded in many places
        const result = {
            forward: new Cartesian3(),
            up: new Cartesian3(),
            right: new Cartesian3(),
            left: new Cartesian3(),
            down: new Cartesian3(),
            backward: new Cartesian3(),
        };
        const forwardDir = new Cartesian3();
        Cartesian3.subtract(endPoint, startPoint, forwardDir);
        // console.log("forwardDir", forwardDir, endPoint, startPoint);
        if (forwardDir.equals(Cartesian3.ZERO)) {
            console.log("reached here");
        }
        if (forwardDir.x === 0) {
            console.log("Break here");
        }
        Cartesian3.normalize(forwardDir, result.forward);
        // console.log("reached here 1", forwardDir);
        Cartesian3.negate(result.forward, result.backward);
        if (!upDir) {
            const planeCarto = Cartographic.fromCartesian(startPoint);
            upDir = new Cartesian3();
            const upPoint = Cartesian3.fromRadians(planeCarto.longitude, planeCarto.latitude, planeCarto.height + 100);
            Cartesian3.subtract(upPoint, startPoint, upDir);
        }
        Cartesian3.normalize(upDir, result.up);
        // console.log("reached here 2", upDir);
        Cartesian3.negate(result.up, result.down);
        const leftDir = new Cartesian3();
        Cartesian3.cross(upDir, forwardDir, leftDir);
        // console.log("leftDir", leftDir);
        Cartesian3.normalize(leftDir, result.left);
        // console.log("reached here 3", leftDir);
        Cartesian3.negate(result.left, result.right);
        return result;
    }
    EarthBrainUtils.getDirectionOfPointOnSitePath = getDirectionOfPointOnSitePath;
    function addSiteDirection(dir1, dir2) {
        const addVector = (v1, v2, result) => {
            Cartesian3.add(v1, v2, result);
            if (Cartesian3.magnitude(result) === 0) {
                console.error("result is 0");
            }
            return Cartesian3.normalize(result, result);
        };
        return {
            up: addVector(dir1.up, dir2.up, new Cartesian3()),
            down: addVector(dir1.down, dir2.down, new Cartesian3()),
            forward: addVector(dir1.forward, dir2.forward, new Cartesian3()),
            backward: addVector(dir1.backward, dir2.backward, new Cartesian3()),
            right: addVector(dir1.right, dir2.right, new Cartesian3()),
            left: addVector(dir1.left, dir2.left, new Cartesian3()),
        };
    }
    EarthBrainUtils.addSiteDirection = addSiteDirection;
    /**
     * This function will add KEY:VALUE pair at INDEX in MAP
     * @param index INDEX at which this key and value needs to be updated
     * @param key KEY that will get added at index in Map
     * @param value VALUE that will get added at index in Map
     * @param map MAP that will updated at index using KEY and VALUE
     */
    function insertKeyValuePairAtIndex(index, key, value, map) {
        if (index <= map.size) {
            const arr = Array.from(map);
            arr.splice(index, 0, [key, value]);
            map.clear();
            arr.forEach(([k, v]) => map.set(k, v));
        }
    }
    EarthBrainUtils.insertKeyValuePairAtIndex = insertKeyValuePairAtIndex;
    function renderRay(ray, viewer, length = 1000, color = Color.RED) {
        const origin = ray.origin.clone();
        const destination = Cartesian3.add(ray.origin, Cartesian3.multiplyByScalar(ray.direction, length, new Cartesian3()), new Cartesian3());
        const entities = [];
        entities.push(createLineEntity(viewer, "renderrayline", [origin, destination], {
            width: 3,
            color: color,
            outlineColor: color,
            outlineWidth: 1,
            clampToGround: false,
            epsilonGapBetWeenMeshAndPolyline: 0.1,
        }));
        // entities.push(createSphere(viewer, origin, Color.GREEN));
        // entities.push(createSphere(viewer, destination, Color.BLUE));
        // viewer.scene.requestRender();
        return entities;
    }
    EarthBrainUtils.renderRay = renderRay;
    function createArrow(viewer, positions, width, color) {
        if (!positions) {
            return;
        }
        const arrowEntity = viewer.entities.add({
            description: "Preview Arrow for Vertical Dragging.",
            polyline: {
                positions: positions,
                width: width,
                arcType: ArcType.NONE,
                material: new PolylineArrowMaterialProperty(color),
                depthFailMaterial: new PolylineArrowMaterialProperty(color),
            },
        });
        // viewer.scene.requestRender();
        return arrowEntity;
    }
    EarthBrainUtils.createArrow = createArrow;
    function convertMeterToFeet(distanceInMeter) {
        return distanceInMeter * 3.28084;
    }
    EarthBrainUtils.convertMeterToFeet = convertMeterToFeet;
    function convertFeetToMeter(distanceInFeet) {
        return distanceInFeet / 3.28084;
    }
    EarthBrainUtils.convertFeetToMeter = convertFeetToMeter;
    /**
     * Checks if ray intersects provided entities.
     * Order of entites matters as first intersection found will be returned
     * @param ray
     * @param data can be TilSet Road or FlatGround
     * @returns
     */
    function getRayIntesectionOnData(ray, data) {
        let pointOnTerrain = undefined;
        const intersectionData = [];
        for (const entity of data) {
            const p = entity.rayCast(ray);
            p && intersectionData.push(p);
        }
        const distances = intersectionData.map((p) => Cartesian3.distance(p, ray.origin));
        pointOnTerrain =
            intersectionData[distances.indexOf(Math.min(...distances))];
        return pointOnTerrain;
    }
    EarthBrainUtils.getRayIntesectionOnData = getRayIntesectionOnData;
    function intersectAgainstIGeometry(ray, geometry) {
        if (!EarthBrainUtils.isValidRay(ray)) {
            console.warn("Invalid ray");
            return undefined;
        }
        let result = undefined;
        for (let i = 0; i < geometry.indices.length; i += 3) {
            const p1 = Cartesian3.fromArray(geometry.vertices, geometry.indices[i] * 3);
            const p2 = Cartesian3.fromArray(geometry.vertices, geometry.indices[i + 1] * 3);
            const p3 = Cartesian3.fromArray(geometry.vertices, geometry.indices[i + 2] * 3);
            result = IntersectionTests.rayTriangle(ray, p1, p2, p3);
            if (result) {
                break;
            }
        }
        return result;
    }
    EarthBrainUtils.intersectAgainstIGeometry = intersectAgainstIGeometry;
    function isPointAboveTheTileset(point, tileSet) {
        let isPointAboveReference = false;
        const absoluteUpperPoint = new Cartesian3(point.x, point.y, point.z + 1000);
        const rayTowardsEarth = new Cartesian3();
        Cartesian3.normalize(Cartesian3.subtract(point, absoluteUpperPoint, rayTowardsEarth), rayTowardsEarth);
        const ray = new Ray(absoluteUpperPoint, rayTowardsEarth);
        const interesctionData = [tileSet];
        const pointOnTerrain = EarthBrainUtils.getRayIntesectionOnData(ray, interesctionData);
        /**
         * We are checking point is on top of the tilese/terrian or not.
         * For that we are ray casting from absolute height of point toward the terrain and checking the intersection on it.
         * And if we get any intersection point we consider that point is on top of terrain. if there is no intersection on terrain then that point is not on terrain.
         * But if the point is not in the render screen then ray intersection on globe will not be done by default by  So we will get this warning.
         */
        if (!pointOnTerrain) {
            // EarthBrainUtils.renderRay(ray, viewer);
            console.warn("Ray is out of rendered area when checking point above the terrain.");
            isPointAboveReference = false;
        }
        else {
            const distanceToOriginal = Cartesian3.distanceSquared(absoluteUpperPoint, point);
            const distanceToTerrain = Cartesian3.distanceSquared(absoluteUpperPoint, pointOnTerrain);
            isPointAboveReference = distanceToOriginal <= distanceToTerrain;
        }
        return isPointAboveReference;
    }
    EarthBrainUtils.isPointAboveTheTileset = isPointAboveTheTileset;
    function createEntityFromIndexedGeometry(geom, geomColor, id, showWireFrame = false) {
        try {
            const geometry = new Geometry({
                //@ts-ignore
                attributes: {
                    position: new GeometryAttribute({
                        componentDatatype: ComponentDatatype.DOUBLE,
                        componentsPerAttribute: 3,
                        values: new Float64Array(geom.vertices),
                    }),
                },
                vertexFormat: VertexFormat.POSITION_ONLY,
                indices: new Uint32Array(geom.indices),
                primitiveType: PrimitiveType.TRIANGLES,
                boundingSphere: BoundingSphere.fromVertices(geom.vertices),
            });
            GeometryPipeline.computeNormal(geometry);
            if (showWireFrame) {
                GeometryPipeline.toWireframe(geometry);
            }
            const instance = new GeometryInstance({
                geometry,
                modelMatrix: Matrix4.IDENTITY,
                attributes: {
                    color: ColorGeometryInstanceAttribute.fromColor(geomColor),
                },
                id: id,
            });
            const primitive = new Primitive({
                geometryInstances: instance,
                allowPicking: true,
                appearance: new PerInstanceColorAppearance({
                    flat: false,
                    translucent: geomColor.alpha === 1.0 ? false : true,
                    faceForward: true,
                }),
                asynchronous: false,
            });
            return primitive;
        }
        catch (e) {
            console.error(e);
        }
    }
    EarthBrainUtils.createEntityFromIndexedGeometry = createEntityFromIndexedGeometry;
    /**
     * Function returns a point at givien direction at givin distance.
     * @param start Start point from which we need a point
     * @param dir Direction toward we need a point
     * @param distance Distance at which we need a point
     * @returns Point at givien direction at distance
     */
    function getDistantCartesian(start, dir, distance) {
        const position = Cartesian3.multiplyByScalar(dir, distance, new Cartesian3());
        return Cartesian3.add(start, position, position);
    }
    EarthBrainUtils.getDistantCartesian = getDistantCartesian;
    /**
     * Function returns a point at a distance from start point on a line.
     * @param start
     * @param end
     * @param distance
     */
    function getDistantCartesianOnALine(start, end, distance) {
        return EarthBrainUtils.getDistantCartesian(start, EarthBrainUtils.getDirBetweenTwoCartesians(start, end), distance);
    }
    EarthBrainUtils.getDistantCartesianOnALine = getDistantCartesianOnALine;
    /**
     * Function returns direction from start point toward a end point
     * @param start Start point from which we need a direction
     * @param end End point toward we need a direction
     * @returns Direction
     */
    function getNormalizedDirection(start, end) {
        const diff = Cartesian3.subtract(end, start, new Cartesian3());
        return Cartesian3.normalize(diff, diff);
    }
    EarthBrainUtils.getNormalizedDirection = getNormalizedDirection;
    /**
     * Returns the linear interpolated points between start point and end point
     * @param startPoint
     * @param endPoint
     * @param interpolationPitch
     * @param inclusive
     */
    function getLinearInterpolation(startPoint, endPoint, interpolationPitch, includeStart, includeEnd, checkEndDivisionPitch = false) {
        const result = [];
        const dir = EarthBrainUtils.getDirBetweenTwoCartesians(startPoint, endPoint);
        const dist = Cartesian3.distance(startPoint, endPoint);
        const divisions = Math.floor(dist / interpolationPitch);
        if (includeStart) {
            result.push(startPoint);
        }
        //get the interpolation points
        for (let i = 1; i <= divisions; ++i) {
            const point = EarthBrainUtils.getCartesianPointAtDistanceFromCartesian(startPoint, dir, i * interpolationPitch);
            if (checkEndDivisionPitch && i === divisions - 1) {
                const distance = Cartesian3.distance(point, endPoint);
                if (distance > interpolationPitch) {
                    result.push(point);
                }
            }
            else {
                result.push(point);
            }
        }
        //push the remaining distance interpolation point to complete the dist between two points.
        if (includeEnd) {
            result.push(endPoint);
        }
        return result;
    }
    EarthBrainUtils.getLinearInterpolation = getLinearInterpolation;
    /**
     * Returns the unit direction between two cartesian points from start to end
     * @param start
     * @param end
     * @returns unit direction vector
     */
    function getDirBetweenTwoCartesians(start, end) {
        try {
            const res = Cartesian3.subtract(end, start, new Cartesian3());
            Cartesian3.normalize(res, res);
            return res;
        }
        catch (e) {
            console.warn("getDirBetweenTwoCartesians error", e);
        }
    }
    EarthBrainUtils.getDirBetweenTwoCartesians = getDirBetweenTwoCartesians;
    /**
     * returns the cartesian point a distance from Cartesian3 point
     * @param start Cartesian3 point from which we need to find the distance
     * @param direction Cartesian3 direction along which we need to find the point
     * @param distance the distance at which we need to find distance
     * @returns cartesian point at distance
     */
    function getCartesianPointAtDistanceFromCartesian(start, direction, distance) {
        const res = new Cartesian3();
        Cartesian3.multiplyByScalar(direction, distance, res);
        Cartesian3.add(start, res, res);
        return res;
    }
    EarthBrainUtils.getCartesianPointAtDistanceFromCartesian = getCartesianPointAtDistanceFromCartesian;
    /**
     * Merge source geometry into target geometry
     * @param target
     * @param source
     */
    function mergeIGeom(target, source) {
        const indexOffset = target.vertices.length / 3;
        for (let i = 0; i < source.vertices.length; ++i) {
            target.vertices.push(source.vertices[i]);
        }
        for (const index of source.indices) {
            target.indices.push(index + indexOffset);
        }
        return target;
    }
    EarthBrainUtils.mergeIGeom = mergeIGeom;
    /**
     * Remove Duplicate Vertices from a geometry & return a new reference of geometry
     * @param geom IGeometry
     * @returns IGeometry
     */
    function removeDuplicateVertices(geom) {
        const uniqueGeometry = {
            vertices: [],
            indices: [],
        };
        const uniqueVertexIndexMap = new Map();
        const getKey = (x, y, z) => {
            return `${x} ${y} ${z}`;
        };
        for (let i = 0; i < geom.indices.length; i++) {
            const p = Cartesian3.fromArray(geom.vertices, geom.indices[i] * 3);
            const key = getKey(p.x, p.y, p.z);
            const originalIndex = uniqueVertexIndexMap.get(key);
            if (originalIndex !== undefined) {
                uniqueGeometry.indices.push(originalIndex);
            }
            else {
                uniqueGeometry.vertices.push(p.x, p.y, p.z);
                uniqueGeometry.indices.push(uniqueGeometry.vertices.length / 3 - 1);
                uniqueVertexIndexMap.set(key, uniqueGeometry.vertices.length / 3 - 1);
            }
        }
        return uniqueGeometry;
    }
    EarthBrainUtils.removeDuplicateVertices = removeDuplicateVertices;
    /**
     * Remove invalid faceIndices from the geometry
     * @param geometry IGeometry
     */
    function removeInvalidFacesFromGeometry(geometry) {
        const validIndices = [];
        for (let i = 0; i < geometry.indices.length; i += 3) {
            const p1 = Cartesian3.fromArray(geometry.vertices, geometry.indices[i] * 3);
            const p2 = Cartesian3.fromArray(geometry.vertices, geometry.indices[i + 1] * 3);
            const p3 = Cartesian3.fromArray(geometry.vertices, geometry.indices[i + 2] * 3);
            const isFaceValid = !(p2.equals(p1) || p2.equals(p3) || p1.equals(p3));
            if (isFaceValid) {
                validIndices.push(geometry.indices[i], geometry.indices[i + 1], geometry.indices[i + 2]);
            }
        }
        geometry.indices = validIndices;
    }
    EarthBrainUtils.removeInvalidFacesFromGeometry = removeInvalidFacesFromGeometry;
    /**
     * Make order of vertices of each face anti clockwise in a geometry
     * @param geometry IGeometry
     * @param upVector Cartesian3 Takes Geodetic Normal
     */
    function makeFaceVerticesOrderAntiClockwise(geometry, upVector) {
        for (let i = 0; i < geometry.indices.length; i += 3) {
            const p1 = Cartesian3.fromArray(geometry.vertices, geometry.indices[i] * 3);
            const p2 = Cartesian3.fromArray(geometry.vertices, geometry.indices[i + 1] * 3);
            const p3 = Cartesian3.fromArray(geometry.vertices, geometry.indices[i + 2] * 3);
            const isFacePointingUp = checkAntiClockwiseTriangle(p1, p2, p3, upVector);
            // If face is not pointing up swap any two indices of the face. Example: 0 1 2 => 1 0 2
            if (isFacePointingUp !== undefined && !isFacePointingUp) {
                const tempIdx = geometry.indices[i + 1];
                geometry.indices[i + 1] = geometry.indices[i];
                geometry.indices[i] = tempIdx;
            }
        }
    }
    EarthBrainUtils.makeFaceVerticesOrderAntiClockwise = makeFaceVerticesOrderAntiClockwise;
    /**
     * Check if the points of a triangle are in anti clockwise order
     * @param p1 Cartesian3
     * @param p2 Cartesian3
     * @param p3 Cartesian3
     * @param upVector Cartesian3 Takes Geodetic Normal
     * @returns Boolean Return true if the points are anti clockwise else return false
     */
    function checkAntiClockwiseTriangle(p1, p2, p3, upVector) {
        if (p1.equals(Cartesian3.ZERO) ||
            p2.equals(Cartesian3.ZERO) ||
            p3.equals(Cartesian3.ZERO)) {
            return undefined;
        }
        const p2p1 = getDirBetweenTwoCartesians(p2, p1);
        const p2p3 = getDirBetweenTwoCartesians(p2, p3);
        const crossProd = Cartesian3.cross(p2p3, p2p1, new Cartesian3());
        if (crossProd.equals(Cartesian3.ZERO)) {
            return undefined;
        }
        Cartesian3.normalize(crossProd, crossProd);
        return Cartesian3.dot(crossProd, upVector) >= 0;
    }
    EarthBrainUtils.checkAntiClockwiseTriangle = checkAntiClockwiseTriangle;
    /**
     * Calculate twice the signed area of a polygon projected on the XY plane using the shoelace formula.
     * This implementation uses the trapezoid variant Σ (x2 − x1)(y2 + y1) which is equivalent to
     * twice the standard shoelace result. The 1/2 factor is intentionally omitted since only
     * the sign is typically used for winding order determination.
     *
     * Sign convention (non-standard): negative = CCW, positive = CW
     * (Note: many implementations use the opposite convention where positive = CCW)
     *
     * @param points Array of Cartesian3 points forming a polygon (at least 3 points required)
     * @returns Twice the signed area (negative for CCW, positive for CW), or 0 for degenerate/invalid input
     */
    function calculateSignedAreaXY(points) {
        if (!points || points.length < 3) {
            return 0; // Return 0 for insufficient points
        }
        let signedTwiceArea = 0;
        for (let i = 0; i < points.length; i++) {
            const curr = points[i];
            const next = points[(i + 1) % points.length];
            signedTwiceArea += (next.x - curr.x) * (next.y + curr.y);
        }
        return signedTwiceArea;
    }
    EarthBrainUtils.calculateSignedAreaXY = calculateSignedAreaXY;
    /**
     * Check if a polygon is in counter-clockwise winding order when viewed from above (Z-up).
     * Uses all points in the polygon for accurate determination, unlike checkAntiClockwiseTriangle
     * which only uses the first 3 points.
     *
     * Note: This assumes points form a proper planar polygon in the XY projection.
     * For truly 3D or non-planar geometries, consider projecting to a local tangent plane first.
     *
     * @param points Array of Cartesian3 points forming a polygon (at least 3 points required)
     * @returns true if counter-clockwise, false if clockwise or indeterminate (degenerate polygon)
     */
    function isPolygonCounterClockwise(points) {
        const signedTwiceArea = calculateSignedAreaXY(points);
        // For degenerate polygons (collinear or duplicate points), area is 0 -> return false
        // This interprets indeterminate as clockwise; consider returning null if needed
        return signedTwiceArea < 0;
    }
    EarthBrainUtils.isPolygonCounterClockwise = isPolygonCounterClockwise;
    /**
     * Returns return the curve path point's doing bazier curve internally
     * @param points Start Mid End in sequnce
     * @param interpolationPitch Pitch to consider
     * @param interpolatedPointFromLastCurve If the curve is connected then this the last point as per pitch
     * @param lengthToConsiderBeforeCurve Distance of the last curve to be considered for pitch
     * @param start Start of the bezier curve
     * @param end End of the bezier curve
     * @returns
     */
    function calculateCurvePathPoint(points, interpolationPitch, start, end) {
        const midPoint_1 = Cartesian3.add(points[0], points[1], new Cartesian3());
        Cartesian3.multiplyByScalar(midPoint_1, 0.5, midPoint_1);
        const midPoint_2 = Cartesian3.add(points[1], points[2], new Cartesian3());
        Cartesian3.multiplyByScalar(midPoint_2, 0.5, midPoint_2);
        const arcLength = Cartesian3.distance(midPoint_1, points[1]) +
            Cartesian3.distance(points[1], midPoint_2);
        const divisions = 2 * Math.floor(arcLength / interpolationPitch);
        // These points are quite close to each other. We need points at interpolation distance
        const bezierPoints = EarthBrainUtils.getBezierPoints(points, divisions, start, end);
        return bezierPoints;
    }
    EarthBrainUtils.calculateCurvePathPoint = calculateCurvePathPoint;
    /**
     *
     * @param points array of three points
     * @param interpolationPitch
     * @param isFirstSegment three points makes two segment, isFirstSegment when true refers to first segment otherwise other segment
     * @returns array of points for first or second segment indicated by isFirstSegment flag
     */
    function calculateCurvePathPointForSegment(points, interpolationPitch, isFirstSegment) {
        const midPoint_1 = Cartesian3.add(points[0], points[1], new Cartesian3());
        Cartesian3.multiplyByScalar(midPoint_1, 0.5, midPoint_1);
        const midPoint_2 = Cartesian3.add(points[1], points[2], new Cartesian3());
        Cartesian3.multiplyByScalar(midPoint_2, 0.5, midPoint_2);
        const arcLength = Cartesian3.distance(midPoint_1, points[1]) +
            Cartesian3.distance(points[1], midPoint_2);
        const divisions = 2 * Math.floor(arcLength / interpolationPitch);
        // These points are quite close to each other. We need points at interpolation distance
        const bezierPoints = EarthBrainUtils.getBezierPoints(points, divisions, 0, 1);
        let index = 0;
        let distance = Number.MAX_VALUE;
        for (let i = 0; i < bezierPoints.length; ++i) {
            const d = Cartesian3.distance(bezierPoints[i], points[1]);
            if (distance < d) {
                index = i - 1;
                break;
            }
            distance = d;
        }
        if (isFirstSegment) {
            return bezierPoints.slice(0, index + 1);
        }
        else {
            return bezierPoints.slice(index);
        }
    }
    EarthBrainUtils.calculateCurvePathPointForSegment = calculateCurvePathPointForSegment;
    /**
     * traingulates 2 arrays and returns geometry
     * @param firstArray
     * @param secondArray
     */
    function createGeometryFromArrays(firstArray, secondArray) {
        const firstArrLen = firstArray.length;
        const secArrLen = secondArray.length;
        if (firstArrLen === 0 || secArrLen === 0) {
            return undefined;
        }
        const targetGeometry = { indices: [], vertices: [] };
        const indices = [];
        const min = Math.min(firstArray.length, secondArray.length);
        for (let i = 0; i < min - 1; ++i) {
            // 0 0 1 | 1 1 2
            indices.push(i, i + firstArrLen, i + firstArrLen + 1);
            // 0 1 1 | 1 2 2
            indices.push(i, i + firstArrLen + 1, i + 1);
        }
        if (firstArray.length > min) {
            for (let i = firstArray.length - 1; i >= min; --i) {
                indices.push(i, i - 1, firstArrLen + secArrLen - 1);
            }
        }
        if (secondArray.length > min) {
            for (let i = min; i < secondArray.length; ++i) {
                const triStartIndex = i + firstArrLen - 1;
                indices.push(firstArrLen - 1, triStartIndex, triStartIndex + 1);
            }
        }
        const combinedVertices = [];
        combinedVertices.push(...firstArray);
        combinedVertices.push(...secondArray);
        const vertArr = [];
        const indexArr = [];
        let index = -1;
        // All of the indices above are for shared vertexes, now we need to have separate vertex for better shading
        // https://github.com/CesiumGS/cesium/wiki/Geometry-and-Appearances
        for (let i = 0; i < indices.length; i += 3) {
            const v1 = combinedVertices[indices[i]];
            const v2 = combinedVertices[indices[i + 1]];
            const v3 = combinedVertices[indices[i + 2]];
            vertArr.push(v1.x, v1.y, v1.z);
            vertArr.push(v2.x, v2.y, v2.z);
            vertArr.push(v3.x, v3.y, v3.z);
            indexArr.push(++index);
            indexArr.push(++index);
            indexArr.push(++index);
        }
        // Since we are appending the points to existing data,
        // we need to increment the indices calculated for these two arrays
        const offsetBeforeExtending = targetGeometry.vertices.length / 3;
        indexArr.forEach((v, i) => (indexArr[i] = v + offsetBeforeExtending));
        // Pushing all the numbers to the main geomtry
        targetGeometry.vertices.push(...vertArr);
        targetGeometry.indices.push(...indexArr);
        return targetGeometry;
    }
    EarthBrainUtils.createGeometryFromArrays = createGeometryFromArrays;
    /**
     * calculates midPoint between two points
     * @param p1 first point
     * @param p2 second point
     * @returns return midPoint between p1 and p2 cartesians
     */
    function midPoint(p1, p2) {
        const leftCornerMidPoint = Cartesian3.add(p1, p2, new Cartesian3());
        return Cartesian3.multiplyByScalar(leftCornerMidPoint, 0.5, leftCornerMidPoint);
    }
    EarthBrainUtils.midPoint = midPoint;
    /**
     *
     * @param type segement type of entity
     * @param side side left/right/center
     * @param geom -geometry containing indices and vertices
     * @param color color optional parameter
     * @returns entity object . if color is specified then object conatains primitive
     */
    function createIEntity(type, side, geom, color) {
        const primitive = color
            ? createEntityFromIndexedGeometry(geom, color)
            : undefined;
        const entity = {
            type,
            side,
            geom,
            primitive,
        };
        return entity;
    }
    EarthBrainUtils.createIEntity = createIEntity;
    function copyRoadDirection(src, dest) {
        copyCartesian(src.forward, dest.forward);
        copyCartesian(src.backward, dest.backward);
        copyCartesian(src.up, dest.up);
        copyCartesian(src.down, dest.down);
        copyCartesian(src.right, dest.right);
        copyCartesian(src.left, dest.left);
    }
    EarthBrainUtils.copyRoadDirection = copyRoadDirection;
    function getAltitude(point) {
        const cartoGraphicPos = Cartographic.fromCartesian(point);
        return cartoGraphicPos.height;
    }
    EarthBrainUtils.getAltitude = getAltitude;
    function setAltitude(point, altitude) {
        const cartoGraphicPos = Cartographic.fromCartesian(point);
        cartoGraphicPos.height = altitude;
        return Cartographic.toCartesian(cartoGraphicPos);
    }
    EarthBrainUtils.setAltitude = setAltitude;
    function getPointsInCircleArc(center, radius, start, end, normal, interpolationPitch) {
        const result = [];
        result.push(start);
        let loopPoint = start;
        const dirToLoop = Cartesian3.subtract(loopPoint, center, new Cartesian3());
        while (Cartesian3.distance(loopPoint, end) > interpolationPitch) {
            const dirToNext = new Cartesian3();
            Cartesian3.cross(dirToLoop, normal, dirToNext);
            Cartesian3.normalize(dirToNext, dirToNext);
            const nextPoint = Cartesian3.multiplyByScalar(dirToNext, interpolationPitch, new Cartesian3());
            Cartesian3.add(loopPoint, nextPoint, nextPoint);
            Cartesian3.subtract(nextPoint, center, dirToLoop);
            Cartesian3.normalize(dirToLoop, dirToLoop);
            const adjustedNextPoint = Cartesian3.multiplyByScalar(dirToLoop, radius, new Cartesian3());
            Cartesian3.add(center, adjustedNextPoint, adjustedNextPoint);
            result.push(adjustedNextPoint);
            loopPoint = adjustedNextPoint;
        }
        result.push(end);
        return result;
    }
    EarthBrainUtils.getPointsInCircleArc = getPointsInCircleArc;
    /**
     * Computes the distance between ray origin and point of ray & bounding box intersection
     * Borrowed from https://github.com/mrdoob/three.js/blob/ce0b3d0415c4bc87a8b4ad01c5d21cc26c6ec5eb/src/math/Ray.js#L323
     * @param ray Ray
     * @param box Axis Aligned Box
     * @returns distance of intersected point on bounding box from ray origin
     */
    function intersectBoundingBox(ray, box) {
        const result = { start: undefined, end: undefined };
        let tmin;
        let tmax;
        let tymin;
        let tymax;
        let tzmin;
        let tzmax;
        const invdirx = 1 / ray.direction.x;
        const invdiry = 1 / ray.direction.y;
        const invdirz = 1 / ray.direction.z;
        const origin = ray.origin;
        if (invdirx >= 0) {
            tmin = (box.minimum.x - origin.x) * invdirx;
            tmax = (box.maximum.x - origin.x) * invdirx;
        }
        else {
            tmin = (box.maximum.x - origin.x) * invdirx;
            tmax = (box.minimum.x - origin.x) * invdirx;
        }
        if (invdiry >= 0) {
            tymin = (box.minimum.y - origin.y) * invdiry;
            tymax = (box.maximum.y - origin.y) * invdiry;
        }
        else {
            tymin = (box.maximum.y - origin.y) * invdiry;
            tymax = (box.minimum.y - origin.y) * invdiry;
        }
        if (tmin > tymax || tymin > tmax)
            return result;
        if (tymin > tmin || isNaN(tmin))
            tmin = tymin;
        if (tymax < tmax || isNaN(tmax))
            tmax = tymax;
        if (invdirz >= 0) {
            tzmin = (box.minimum.z - origin.z) * invdirz;
            tzmax = (box.maximum.z - origin.z) * invdirz;
        }
        else {
            tzmin = (box.maximum.z - origin.z) * invdirz;
            tzmax = (box.minimum.z - origin.z) * invdirz;
        }
        if (tmin > tzmax || tzmin > tmax)
            return result;
        if (tzmin > tmin || tmin !== tmin)
            tmin = tzmin;
        if (tzmax < tmax || tmax !== tmax)
            tmax = tzmax;
        if (tmax < 0)
            return result;
        result.start = tmin >= 0 ? tmin : undefined;
        result.end = tmax >= 0 ? tmax : undefined;
        return result;
    }
    EarthBrainUtils.intersectBoundingBox = intersectBoundingBox;
    function getFractionalDistanceFromStartPointForAGivenPathPoint(pathPoint, pathPoints) {
        const indexOfPathPoint = pathPoints.findIndex((point) => point.equals(pathPoint));
        if (indexOfPathPoint === -1)
            return;
        const result = pathPoints.reduce((acc, curr, index) => {
            const distanceFromPrev = acc.point
                ? Cartesian3.distance(acc.point, curr)
                : 0;
            return {
                distanceFromFirstPointToPathPoint: index <= indexOfPathPoint
                    ? acc.distanceFromFirstPointToPathPoint + distanceFromPrev
                    : acc.distanceFromFirstPointToPathPoint,
                point: curr,
                totalDistance: acc.totalDistance + distanceFromPrev,
            };
        }, {
            distanceFromFirstPointToPathPoint: 0,
            point: undefined,
            totalDistance: 0,
        });
        return result.distanceFromFirstPointToPathPoint / result.totalDistance;
    }
    EarthBrainUtils.getFractionalDistanceFromStartPointForAGivenPathPoint = getFractionalDistanceFromStartPointForAGivenPathPoint;
    function getIndexForProbableSegmentAndProjectedPoint(point, points) {
        //should be perpendiculat distances with the segment
        const distancesWithSegments = [];
        let projectedPoint;
        for (let i = 0; i < points.length; ++i) {
            const nextIndex = (i + 1) % points.length;
            const segmentVec = Cartesian3.subtract(points[nextIndex], points[i], new Cartesian3());
            const pointVec = Cartesian3.subtract(point, points[i], new Cartesian3());
            const projectedVec = Cartesian3.projectVector(pointVec, segmentVec, new Cartesian3());
            projectedPoint = Cartesian3.add(points[i], projectedVec, new Cartesian3());
            //check if the projected point is within the segment
            const distSeg1 = Cartesian3.distance(points[i], projectedPoint);
            const distSeg2 = Cartesian3.distance(points[nextIndex], projectedPoint);
            const distSeg = Cartesian3.distance(points[i], points[nextIndex]);
            if (distSeg1 + distSeg2 - distSeg <= 0.01) {
                //within the segment
                const distWithSeg = Cartesian3.distance(point, projectedPoint);
                distancesWithSegments.push(distWithSeg);
            }
            else {
                distancesWithSegments.push(Number.NaN);
            }
        }
        const minDistance = Math.min(...distancesWithSegments.filter((d) => !isNaN(d)));
        const segmentIndex = distancesWithSegments.indexOf(minDistance);
        // const segmentIndex = indexOfminDistance < nextIndex ? indexOfminDistance : nextIndex;
        return {
            segmentIndex,
            projectedPoint,
        };
    }
    EarthBrainUtils.getIndexForProbableSegmentAndProjectedPoint = getIndexForProbableSegmentAndProjectedPoint;
    function calculateTriangleArea(p1, p2, p3) {
        const vec1 = Cartesian3.subtract(p2, p1, new Cartesian3());
        const vec2 = Cartesian3.subtract(p3, p1, new Cartesian3());
        const crossProduct = Cartesian3.cross(vec1, vec2, new Cartesian3());
        const area = 0.5 * Cartesian3.magnitude(crossProduct);
        return area;
    }
    EarthBrainUtils.calculateTriangleArea = calculateTriangleArea;
    function tracePointsOnGeometry(geometry, heightMap, points) {
        /**
         * For all trace points there should be one value and it can be undefined.
         */
        if (points.length < 2) {
            return [];
        }
        const start = points[0];
        const end = points[points.length - 1];
        const dir = EarthBrainUtils.getDirBetweenTwoCartesians(new Cartesian3(start.x, start.y, 0), new Cartesian3(end.x, end.y, 0));
        const up = Cartesian3.UNIT_Z;
        const normal = Cartesian3.cross(dir, up, new Cartesian3());
        const center = new Cartesian3();
        Cartesian3.multiplyByScalar(Cartesian3.add(start, end, center), 0.5, center);
        const zBoundedPlane = ZBoundedPlane.fromPointNormal(center, normal);
        const checkObjectExistenceBetweenPoints = (point1, point2, geometry) => {
            // Check if object exists between two points using raycast
            // To improve robustness near boundaries, check at the midpoint and at points with small XY offsets
            const midpoint = Cartesian3.lerp(point1, point2, 0.5, new Cartesian3());
            const pointsNearMid = [
                midpoint,
                new Cartesian3(midpoint.x + BOUNDARY_TOLERANCE, midpoint.y, midpoint.z),
                new Cartesian3(midpoint.x - BOUNDARY_TOLERANCE, midpoint.y, midpoint.z),
                new Cartesian3(midpoint.x, midpoint.y + BOUNDARY_TOLERANCE, midpoint.z),
                new Cartesian3(midpoint.x, midpoint.y - BOUNDARY_TOLERANCE, midpoint.z),
            ];
            return pointsNearMid.some((p) => {
                const ray = new Ray(new Cartesian3(p.x, p.y, p.z + 10000), new Cartesian3(0, 0, -1));
                return (EarthBrainUtils.intersectAgainstIGeometry(ray, geometry) !== undefined);
            });
        };
        const sortBy2DDistanceFromStartPoint = (points, start) => {
            points.sort((a, b) => Cartesian2.distance(start, a) - Cartesian2.distance(start, b)); // Assending Order
        };
        const intersectedPoints = intersectGeometryByPlane(geometry, zBoundedPlane);
        const sameSidePoints = intersectedPoints.filter((p) => {
            const pV = Cartesian2.subtract(p, start, new Cartesian2());
            const dot = Cartesian2.dot(dir, pV);
            return dot >= 0;
        });
        const oppositeSidePoints = intersectedPoints.filter((p) => {
            const pV = Cartesian2.subtract(p, start, new Cartesian2());
            const dot = Cartesian2.dot(dir, pV);
            return dot < 0;
        });
        sortBy2DDistanceFromStartPoint(sameSidePoints, start);
        sortBy2DDistanceFromStartPoint(oppositeSidePoints, start);
        const referenceOnGeom = HeightMapUtils.getHeight(start, heightMap.getObject());
        const newIntersectedPoints = referenceOnGeom && oppositeSidePoints.length > 0
            ? [oppositeSidePoints[0], ...sameSidePoints]
            : sameSidePoints;
        let j = referenceOnGeom && oppositeSidePoints.length > 0 ? 1 : 0;
        const filteredPoints = [];
        for (const point of newIntersectedPoints) {
            //same side points+interpolated points
            if (!filteredPoints[filteredPoints.length - 1] ||
                Cartesian3.distance(filteredPoints[filteredPoints.length - 1], point) >
                    0.00000001) {
                filteredPoints.push(point);
            }
        }
        const tracedPoints = Array(points.length).fill(undefined);
        for (let i = 0; i < points.length; ++i) {
            const v1 = Cartesian2.subtract(points[i], start, new Cartesian2());
            if (filteredPoints[j]) {
                const v2 = Cartesian2.subtract(filteredPoints[j], start, new Cartesian2());
                const d1 = Cartesian2.magnitude(v1);
                const d2 = Cartesian2.magnitude(v2);
                if (d2 < d1) {
                    if (filteredPoints[j + 1]) {
                        if (checkObjectExistenceBetweenPoints(filteredPoints[j], filteredPoints[j + 1], geometry)) {
                            const dir = EarthBrainUtils.getDirBetweenTwoCartesians(filteredPoints[j], filteredPoints[j + 1]);
                            const dirxy = EarthBrainUtils.getDirBetweenTwoCartesians(new Cartesian3(filteredPoints[j].x, filteredPoints[j].y, 0), new Cartesian3(filteredPoints[j + 1].x, filteredPoints[j + 1].y, 0));
                            Cartesian3.multiplyByScalar(dirxy, d1 - d2, dirxy);
                            const b = d1 - d2;
                            const D = Cartesian3.distance(filteredPoints[j], filteredPoints[j + 1]);
                            const B = Cartesian2.distance(filteredPoints[j], filteredPoints[j + 1]);
                            const d = (b * D) / B;
                            Cartesian3.add(filteredPoints[j], Cartesian3.multiplyByScalar(dir, d, dir), dir);
                            tracedPoints[i] = new Cartesian3(points[i].x, points[i].y, dir.z);
                        }
                    }
                    ++j;
                }
                else if (d2 > d1) {
                    if (filteredPoints[j - 1]) {
                        if (checkObjectExistenceBetweenPoints(filteredPoints[j], filteredPoints[j - 1], geometry)) {
                            const dir = EarthBrainUtils.getDirBetweenTwoCartesians(filteredPoints[j], filteredPoints[j - 1]);
                            const dirxy = EarthBrainUtils.getDirBetweenTwoCartesians(new Cartesian3(filteredPoints[j].x, filteredPoints[j].y, 0), new Cartesian3(filteredPoints[j - 1].x, filteredPoints[j - 1].y, 0));
                            Cartesian3.multiplyByScalar(dirxy, d2 - d1, dirxy);
                            const b = d2 - d1;
                            const D = Cartesian3.distance(filteredPoints[j], filteredPoints[j - 1]);
                            const B = Cartesian2.distance(filteredPoints[j], filteredPoints[j - 1]);
                            const d = (b * D) / B;
                            Cartesian3.add(filteredPoints[j], Cartesian3.multiplyByScalar(dir, d, dir), dir);
                            tracedPoints[i] = new Cartesian3(points[i].x, points[i].y, dir.z);
                        }
                    }
                }
                else {
                    tracedPoints[i] = filteredPoints[j];
                    ++j;
                }
            }
        }
        return tracedPoints;
    }
    EarthBrainUtils.tracePointsOnGeometry = tracePointsOnGeometry;
    /**
     * Retrieves the terrain height at a specific cartographic location (latitude and longitude).
     *
     * @param latitude - The latitude of the location.
     * @param longitude - The longitude of the location.
     * @param viewer - The Cesium Viewer instance containing the terrain provider.
     * @returns Promise<number> - A promise that resolves to the terrain height at the specified location.
     */
    async function getTerrainHeightFromCartographic(longitude, latitude, viewer) {
        const originCartographic = Cartographic.fromDegrees(longitude, latitude);
        // 	const upPoint = Cartesian3.fromRadians(
        // 		originCartographic.longitude,
        // 		originCartographic.latitude,
        // 		originCartographic.height + 100,
        // 	);
        // 	const origin = Cartesian3.fromRadians(longitude, latitude, 0);
        // const dirTowardsEarth = new Cartesian3();
        // Cartesian3.normalize(
        // 	Cartesian3.subtract(origin, upPoint, dirTowardsEarth),
        // 	dirTowardsEarth,
        // );
        // const ray = new Ray(upPoint, dirTowardsEarth);
        // const hitPoint = viewer.scene.globe.pick(ray, viewer.scene);
        // if(hitPoint){
        // 	const height1 = Cartographic.fromCartesian(hitPoint).height;
        // 	console.log("height1 ", height1);
        // }
        // const height = viewer.scene.globe.getHeight(originCartographic);
        const originCartographicWithHeightArray = await sampleTerrainMostDetailed(viewer.terrainProvider, [originCartographic], true);
        // console.log("height ",height, originCartographicWithHeightArray[0].height, height - originCartographicWithHeightArray[0].height);
        return originCartographicWithHeightArray[0].height;
    }
    EarthBrainUtils.getTerrainHeightFromCartographic = getTerrainHeightFromCartographic;
    /**
     * Calculates the corner positions of an image on the Earth's surface given its center coordinates, dimensions, height, and rotation.
     *
     * @param {number} latitude - The latitude of the image center in degrees.
     * @param {number} longitude - The longitude of the image center in degrees.
     * @param {number} imageWidth - The width of the image in meters.
     * @param {number} imageHeight - The height of the image in meters.
     * @param {number} [height=0] - The height above the ellipsoid in meters.
     * @param {number} [rotationDegree=0] - The rotation of the image in degrees.
     * @returns {Array<Cartesian3>} An array of Cartesian3 positions representing the corners of the image.
     */
    function getCornerPositions(longitude, latitude, imageWidth, imageHeight, height = 0, rotationDegree = 0) {
        // calculate the corner vertices of the image
        const radius = Ellipsoid.WGS84.maximumRadius;
        const meterPerLatitude = (2 * Math.PI * radius) / 360;
        const meterPerLongitude = (2 * Math.PI * radius * Math.cos(CesiumMath.toRadians(latitude))) / 360;
        const latitudeDiff = (imageHeight * 0.5) / meterPerLatitude;
        const longitudeDiff = (imageWidth * 0.5) / meterPerLongitude;
        const topLeftLat = latitude + latitudeDiff;
        const topLeftLon = longitude - longitudeDiff;
        const topRightLat = latitude + latitudeDiff;
        const topRightLon = longitude + longitudeDiff;
        const bottomLeftLat = latitude - latitudeDiff;
        const bottomLeftLon = longitude - longitudeDiff;
        const bottomRightLat = latitude - latitudeDiff;
        const bottomRightLon = longitude + longitudeDiff;
        const cornerVertices = [
            Cartesian3.fromDegrees(topLeftLon, topLeftLat, height),
            Cartesian3.fromDegrees(topRightLon, topRightLat, height),
            Cartesian3.fromDegrees(bottomLeftLon, bottomLeftLat, height),
            Cartesian3.fromDegrees(bottomRightLon, bottomRightLat, height),
        ];
        // rotate the image around the center of the image and the normal vector of the image
        // 1. calculate the center of the image
        const center = Cartesian3.midpoint(cornerVertices[0], cornerVertices[2], new Cartesian3());
        // 2. calculate the normal vector of the image from edges
        const edge1 = Cartesian3.subtract(cornerVertices[1], cornerVertices[0], new Cartesian3());
        const edge2 = Cartesian3.subtract(cornerVertices[3], cornerVertices[0], new Cartesian3());
        const normal = Cartesian3.cross(edge1, edge2, new Cartesian3());
        Cartesian3.normalize(normal, normal); // 法線ベクトルを正規化
        // 3. calculate the rotation angle in radians
        const angleInRadians = CesiumMath.toRadians(rotationDegree);
        // 4. calculate the rotation matrix
        const quaternion = Quaternion.fromAxisAngle(normal, angleInRadians);
        const rotationMatrix = Matrix3.fromQuaternion(quaternion);
        // 5. rotate the corner vertices
        const rotatedVertices = cornerVertices.map((vertex) => {
            const translation = Cartesian3.subtract(vertex, center, new Cartesian3());
            const rotatedTranslation = Matrix3.multiplyByVector(rotationMatrix, translation, new Cartesian3());
            const result = Cartesian3.add(center, rotatedTranslation, new Cartesian3());
            return result;
        });
        return rotatedVertices;
    }
    EarthBrainUtils.getCornerPositions = getCornerPositions;
    function isValidRay(ray) {
        if (!ray || !ray.origin || !ray.direction) {
            return false;
        }
        const d = ray.direction;
        if (isNaN(d.x) || isNaN(d.y) || isNaN(d.z)) {
            return false;
        }
        if (Cartesian3.equals(d, Cartesian3.ZERO)) {
            return false;
        }
        const mag = Cartesian3.magnitude(d);
        if (mag === 0 || isNaN(mag)) {
            return false;
        }
        return true;
    }
    EarthBrainUtils.isValidRay = isValidRay;
})(EarthBrainUtils || (EarthBrainUtils = {}));
//# sourceMappingURL=earthbrainutility.js.map